import { useCallback } from "react";
import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import type { Lead, Salesperson } from "../../types/lead";
import type { Location } from "../../types/location";
import type { DeepgramStreamingEngine } from "@/lib/asr/deepgramStreamingEngine";
import { useRecentLeads } from "@/hooks/useRecentLeads";
import { useDeviceData } from "@/hooks/useDeviceData";
import { LeadDeviceDevTools } from "./sections/LeadDeviceDevTools";
import { LeadDeviceAsrDiagnosticsPanel } from "./sections/LeadDeviceAsrDiagnosticsPanel";

type UseLeadWorkspaceOpsParams = {
  recentLeadsPool: Lead[];
  showAllRecentLeads: boolean;
  recentFilter: "all" | "today" | "unsent";
  recentSort: "recent" | "oldest" | "status";
  managerListFilters?: import("@/lib/lead/leadsManagerFilters").LeadsManagerListFilters;
  pendingSendLead: Lead | null;
  setPendingSendLead: Dispatch<SetStateAction<Lead | null>>;
  setDuplicateMatches: Dispatch<SetStateAction<Lead[]>>;
  setDuplicateReason: Dispatch<SetStateAction<string | null>>;
  setPendingLocationSend: Dispatch<SetStateAction<Lead | null>>;
  setLeads: Dispatch<SetStateAction<Lead[]>>;
  setCurrentLead: Dispatch<SetStateAction<Lead | null>>;
  setTranscript: Dispatch<SetStateAction<string>>;
  setIsListening: Dispatch<SetStateAction<boolean>>;
  setMergeSuggestion: Dispatch<SetStateAction<{ sourceId: string; reason: string } | null>>;
  currentLeadRef: MutableRefObject<Lead | null>;
  transcriptRef: MutableRefObject<string>;
  listeningRef: MutableRefObject<boolean>;
  leadWorkspaceStorageKey: string;
  salesperson: Salesperson | null;
  location: Location | null;
  leads: Lead[];
  setSalesperson: Dispatch<SetStateAction<Salesperson | null>>;
  setLocation: Dispatch<SetStateAction<Location | null>>;
  appVersion: string;
  /** Optional — when provided, mounts the Phase 1 Deepgram diagnostics dev panel. */
  asrEngineRef?: MutableRefObject<DeepgramStreamingEngine | null>;
};

export function useLeadWorkspaceOps(params: UseLeadWorkspaceOpsParams) {
  const removeLeadFromWorkspace = useCallback(
    (leadId: string) => {
      params.setLeads((prev) => prev.filter((l) => l.id !== leadId));
      if (params.currentLeadRef.current?.id === leadId) {
        params.setCurrentLead(null);
        params.currentLeadRef.current = null;
        params.setTranscript("");
        params.transcriptRef.current = "";
        params.setIsListening(false);
        params.listeningRef.current = false;
        params.setMergeSuggestion(null);
      }
      const wasPendingDup = params.pendingSendLead?.id === leadId;
      params.setPendingSendLead((p) => (p?.id === leadId ? null : p));
      if (wasPendingDup) {
        params.setDuplicateMatches([]);
        params.setDuplicateReason(null);
      }
      params.setPendingLocationSend((p) => (p?.id === leadId ? null : p));
    },
    [params]
  );

  const {
    filteredRecentLeads,
    visibleRecentLeads,
    hasMoreLeads,
    todaysLeads,
    todaysSent,
    todaysUnsent,
    totalUnsent,
    queuedCount,
  } = useRecentLeads({
    leads: params.recentLeadsPool,
    showAllRecentLeads: params.showAllRecentLeads,
    recentFilter: params.recentFilter,
    recentSort: params.recentSort,
    managerListFilters: params.managerListFilters,
  });

  const { resetAllData, resetSetupOnly, copyLeadsJson } = useDeviceData<Salesperson, Location>({
    storageKey: params.leadWorkspaceStorageKey,
    version: params.appVersion,
    salesperson: params.salesperson,
    location: params.location,
    leads: params.leads,
    setSalesperson: params.setSalesperson,
    setLocation: params.setLocation,
    setLeads: params.setLeads,
    setCurrentLead: params.setCurrentLead,
    currentLeadRef: params.currentLeadRef,
    transcriptRef: params.transcriptRef,
    listeningRef: params.listeningRef,
    setTranscript: params.setTranscript,
    setIsListening: params.setIsListening,
    setMergeSuggestion: params.setMergeSuggestion,
  });

  const leadsDevToolsNode = (
    <>
      <LeadDeviceDevTools
        resetSetupOnly={resetSetupOnly}
        resetAllData={resetAllData}
        copyLeadsJson={copyLeadsJson}
      />
      {params.asrEngineRef ? <LeadDeviceAsrDiagnosticsPanel asrEngineRef={params.asrEngineRef} /> : null}
    </>
  );

  return {
    removeLeadFromWorkspace,
    filteredRecentLeads,
    visibleRecentLeads,
    hasMoreLeads,
    todaysLeads,
    todaysSent,
    todaysUnsent,
    totalUnsent,
    queuedCount,
    leadsDevToolsNode,
  };
}
